import { useEffect, useState } from "react";
import { Pause, Play, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * QuickPauseChip — Swiggy-style header chip that lets a merchant pause
 * a restaurant for 30 min / 60 min / Rest of the day, or resume.
 *
 * `restaurants` is the array from `GET /merchant/restaurants`.
 * `restaurantId` (optional) — if the orders page is filtered to one restaurant,
 *   we lock the chip to it; otherwise the chip shows a single-or-aggregate
 *   selector when there's exactly one restaurant.
 */
export default function QuickPauseChip({ restaurants, restaurantId, onChanged }) {
    const [open, setOpen] = useState(false);
    const [busyKey, setBusyKey] = useState(null);
    const [now, setNow] = useState(Date.now());
    useEffect(() => { const i = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(i); }, []);

    // Resolve which restaurant this chip controls.
    const target = restaurantId
        ? restaurants.find((r) => r.id === restaurantId)
        : (restaurants.length === 1 ? restaurants[0] : null);

    if (!target) return null;

    const paused = !!target.manual_closed;
    const pausedUntil = target.paused_until ? new Date(target.paused_until).getTime() : null;
    const minsLeft = pausedUntil ? Math.max(0, Math.round((pausedUntil - now) / 60000)) : null;

    const action = async (duration) => {
        setBusyKey(duration);
        try {
            await api.post(`/merchant/restaurants/${target.id}/quick-pause?duration=${duration}`);
            const labels = { "30": "Paused for 30 min", "60": "Paused for 60 min", "REST_OF_DAY": "Paused for the day", "RESUME": "You're back online" };
            toast.success(labels[duration]);
            setOpen(false);
            onChanged && onChanged();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusyKey(null); }
    };

    return (
        <div className="relative">
            <button
                data-testid="quick-pause-chip"
                onClick={() => setOpen((v) => !v)}
                className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl border transition ${paused ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
                title={target.name}
            >
                {paused ? <Pause size={13} /> : <Play size={13} />}
                {paused
                    ? (minsLeft !== null && minsLeft < 12 * 60 ? `Paused · ${minsLeft}m left` : "Paused")
                    : "Accepting orders"}
                <ChevronDown size={12} />
            </button>

            {open && (
                <>
                    <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 z-40 w-64 bg-card border border-border rounded-2xl shadow-xl p-2" data-testid="quick-pause-menu">
                        <p className="px-2 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{target.name}</p>
                        {paused ? (
                            <Item testid="qp-resume" busy={busyKey === "RESUME"} onClick={() => action("RESUME")} icon={<Play size={14} className="text-emerald-600" />} title="Resume now" subtitle="Start accepting orders again" />
                        ) : (
                            <>
                                <Item testid="qp-30"  busy={busyKey === "30"}  onClick={() => action("30")}  icon={<Pause size={14} className="text-amber-700" />} title="Pause for 30 min" subtitle="Auto-resume after 30 minutes" />
                                <Item testid="qp-60"  busy={busyKey === "60"}  onClick={() => action("60")}  icon={<Pause size={14} className="text-amber-700" />} title="Pause for 60 min" subtitle="Auto-resume after 60 minutes" />
                                <Item testid="qp-day" busy={busyKey === "REST_OF_DAY"} onClick={() => action("REST_OF_DAY")} icon={<Pause size={14} className="text-red-700" />} title="Pause for the day" subtitle="Auto-resume tomorrow 9:00 AM" />
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function Item({ icon, title, subtitle, onClick, busy, testid }) {
    return (
        <button data-testid={testid} onClick={onClick} disabled={busy} className="w-full flex items-start gap-2 text-left p-2 rounded-xl hover:bg-muted disabled:opacity-50">
            <span className="mt-0.5">{busy ? <Loader2 size={14} className="animate-spin" /> : icon}</span>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-bold">{title}</p>
                <p className="text-[11px] text-muted-foreground">{subtitle}</p>
            </div>
        </button>
    );
}
